import { supabase } from './supabase'

export interface ParsedMBLData {
  doc_type: 'MBL' | 'HBL'
  mbl_number?: string
  hbl_number?: string
  shipper_name?: string
  shipper_address?: string
  consignee_name?: string
  consignee_address?: string
  notify_party?: string
  port_of_loading?: string
  port_of_discharge?: string
  vessel_name?: string
  voyage_number?: string
  container_numbers?: string[]
  goods_description?: string
  total_weight_kg?: number
  total_volume_cbm?: number
  eta?: string
  etd?: string
}

export async function parseDocumentWithAI(base64Content: string): Promise<ParsedMBLData> {
  try {
    const { data, error } = await supabase.functions.invoke('parse-document', {
      body: {
        document: base64Content
      }
    })

    if (error) {
      throw new Error(error.message || 'Failed to parse document')
    }

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response from parser')
    }

    return data as ParsedMBLData
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Document parsing failed'
    throw new Error(message)
  }
}

export interface AutoJobsData {
  waybill_number?: string
  mbl_number?: string
  voyage_number?: string
  carrier?: string
  vessel_name?: string
  export_reference?: string
  transport_mode?: string
  direction?: string
  doc_type?: string
  flight_vessel?: string
  origin_country?: string
  dest_country?: string
  eta?: string
  etd?: string
  shipper?: { name?: string; address?: string }
  consignee?: { name?: string; address?: string; contact?: string; eori?: string; vat_no?: string }
  ports?: { loading?: string; discharge?: string; delivery?: string }
  container?: { number?: string; seal?: string; type?: string; packages?: string }
  cargo?: {
    description?: string
    gross_weight_kgs?: number
    net_weight_kgs?: number
    volume_cbm?: number
  }
  summary?: string
}

export type ParsedDocumentResult = AutoJobsData | { master?: AutoJobsData; details?: AutoJobsData[] }

interface FileWithContent {
  fileName: string
  mimeType: string
  fileContent: string
  role: string
}

export async function parseDocumentAuto(
  request:
    | string
    | { mblContent: string; hblContent: string; mblMimeType?: string; hblMimeType?: string }
    | { mode: 'single' | 'mbl_hbl' | 'bundled' | 'manifest' | 'invoices'; mblFile: FileWithContent | null; hblFiles: FileWithContent[] },
  mimeType?: string,
  scenario?: 'single' | 'mbl_hbl' | 'bundled' | 'manifest' | 'invoices'
): Promise<ParsedDocumentResult> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    if (!supabaseUrl) throw new Error('Missing VITE_SUPABASE_URL')
    if (!anonKey) throw new Error('Missing VITE_SUPABASE_ANON_KEY')

    let requestBody: any
    let finalScenario: string

    // Handle new format: { mode, mblFile, hblFiles }
    if (
      typeof request === 'object' &&
      'mode' in request &&
      'mblFile' in request &&
      'hblFiles' in request
    ) {
      const { mode, mblFile, hblFiles } = request as any
      finalScenario = mode

      // Z-fix: mblFile is optional — omitted entirely from the wire payload when
      // null, so the backend sees no mblFile key at all (rather than re-sending
      // and re-parsing the MBL on every follow-up call in a multi-file batch).
      requestBody = { scenario: mode }
      if (mblFile) {
        requestBody.mblFile = {
          fileName: mblFile.fileName,
          mimeType: mblFile.mimeType,
          fileContent: mblFile.fileContent
        }
      }
      if (hblFiles && hblFiles.length > 0) {
        requestBody.hblFiles = hblFiles.map((f: any) => ({
          fileName: f.fileName,
          mimeType: f.mimeType,
          fileContent: f.fileContent
        }))
      }
      console.log('[parseDocumentAuto] Sending bundled-scenario request', {
        scenario: mode,
        hasMblFile: !!mblFile,
        hblFileCount: hblFiles?.length || 0,
        hblFileNames: (hblFiles || []).map((f: any) => f.fileName).join(', ')
      })
    } else if (typeof request === 'object' && 'mblContent' in request && scenario === 'mbl_hbl') {
      // Legacy format for mbl_hbl: { mblContent, hblContent }
      const base64Content = request as { mblContent: string; hblContent: string; mblMimeType?: string; hblMimeType?: string }
      finalScenario = 'mbl_hbl'
      requestBody = {
        mblContent: base64Content.mblContent,
        hblContent: base64Content.hblContent,
        mblMimeType: base64Content.mblMimeType,
        hblMimeType: base64Content.hblMimeType,
        scenario: 'mbl_hbl'
      }
      console.log(`[parseDocumentAuto] Sending MBL+HBL request (master: 1 file, details: ${base64Content.hblContent ? '1 file' : '0 files'})`, {
        scenario: 'mbl_hbl',
        mblLength: base64Content.mblContent.length,
        hblLength: base64Content.hblContent.length
      })
    } else if (typeof request === 'object' && 'mblContent' in request && (scenario === 'bundled' || scenario === 'manifest' || scenario === 'invoices')) {
      // Legacy format for multi-file scenarios
      const base64Content = request as { mblContent: string; hblContent: string; mblMimeType?: string; hblMimeType?: string }
      finalScenario = scenario!
      requestBody = {
        mblContent: base64Content.mblContent,
        hblContent: base64Content.hblContent,
        mblMimeType: base64Content.mblMimeType,
        hblMimeType: base64Content.hblMimeType,
        scenario
      }
      console.log(`[parseDocumentAuto] Sending MBL+HBL request (master: 1 file, details: ${base64Content.hblContent ? '1 file' : '0 files'})`, {
        scenario,
        mblLength: base64Content.mblContent.length,
        hblLength: base64Content.hblContent.length
      })
    } else {
      // Legacy single file format
      const content = typeof request === 'string' ? request : (request as any).mblContent
      finalScenario = scenario || 'single'
      requestBody = {
        fileContent: content,
        mimeType,
        scenario: finalScenario
      }
      console.log('[parseDocumentAuto] Sending single file request', {
        scenario: finalScenario,
        mimeType,
        contentLength: content.length
      })
    }

    console.log('[parseDocumentAuto] Final body payload:',
      JSON.stringify({
        mode: finalScenario,
        hasFileContent: !!requestBody.fileContent,
        hasMblFile: !!requestBody.mblFile,
        hasHblFiles: !!requestBody.hblFiles,
        hblFilesCount: requestBody.hblFiles?.length,
        bodyKeys: Object.keys(requestBody)
      })
    )

    const response = await fetch(`${supabaseUrl}/functions/v1/parse-document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })

    const responseData = await response.json()

    console.log('[parseDocumentAuto] Response received', {
      status: response.status,
      hasError: responseData?.error,
      contentLength: JSON.stringify(responseData).length
    })

    if (!response.ok) {
      const errorMsg = responseData?.error || responseData?.message || `HTTP ${response.status}`
      const details = responseData?.details || responseData?.parseError || ''
      console.error('[parseDocumentAuto] Parse error:', { errorMsg, details })
      throw new Error(`Parse failed: ${errorMsg}${details ? ` - ${details}` : ''}`)
    }

    if (!responseData || typeof responseData !== 'object') {
      console.error('[parseDocumentAuto] Invalid response type:', typeof responseData)
      throw new Error('Invalid response from parser')
    }

    if ('error' in responseData && responseData.error) {
      console.error('[parseDocumentAuto] Data contains error:', responseData)
      throw new Error(`Parser error: ${responseData.error}`)
    }

    console.log('[parseDocumentAuto] Successfully parsed data with keys:', Object.keys(responseData).join(', '))
    return responseData as AutoJobsData
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Document parsing failed'
    console.error('[parseDocumentAuto] Caught error:', message)
    throw new Error(message)
  }
}
